import * as Sentry from '@sentry/react-native'
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form'
import { notify } from '@/lib/notice'
import { pb } from '@/lib/pocketbase'
import type { UserRole, UserSession } from '@/lib/types'
import type { Users } from '@/types/pbSchema'
import { log } from './logger'

export class OrgAccessError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'OrgAccessError'
    }
}

let lastNetworkErrorToast = 0
const NETWORK_ERROR_TOAST_DEBOUNCE_MS = 2000

let currentUserRole: UserRole | null = null

export function setCurrentUserRole(role: UserRole | null): void {
    currentUserRole = role
}

function showNetworkErrorToast(_event: Sentry.Event): void {
    if (currentUserRole !== 'admin') {
        return
    }

    const now = Date.now()
    if (now - lastNetworkErrorToast < NETWORK_ERROR_TOAST_DEBOUNCE_MS) {
        return
    }
    lastNetworkErrorToast = now

    log.warn('Network error detected')
    notify.error('Network request failed. Please check your connection and try again.')
}

const IGNORED_ERROR_PATTERNS: RegExp[] = [
    /aborted/i,
    /autocancelled/i,
    /failed to start subscription/i,
]

function isIgnoredError(event: Sentry.Event): boolean {
    const strings = [event.message || '']
    for (const exception of event.exception?.values || []) {
        if (exception.value) strings.push(exception.value)
    }
    return strings.some(str => IGNORED_ERROR_PATTERNS.some(pattern => pattern.test(str)))
}

// A ClientResponseError with status 0 is a transport failure (offline, DNS,
// TLS, aborted). One with a real HTTP status is a server response — those
// are bugs we want Sentry to see, not toast-and-swallow. Returns null if
// the event isn't a ClientResponseError at all.
function clientResponseErrorIsTransportFailure(
    exceptionValues: NonNullable<Sentry.Event['exception']>['values']
): boolean | null {
    for (const exception of exceptionValues || []) {
        const type = exception.type?.toLowerCase() || ''
        if (type.startsWith('clientresponseerror')) {
            const match = type.match(/clientresponseerror\s+(\d+)/)
            const status = match ? Number(match[1]) : 0
            return status === 0
        }
    }
    return null
}

function isNetworkErrorEvent(event: Sentry.Event): boolean {
    if (isIgnoredError(event)) return false

    const exceptionValues = event.exception?.values || []
    const transportOnly = clientResponseErrorIsTransportFailure(exceptionValues)
    if (transportOnly !== null) return transportOnly

    const networkPattern = 'network request failed'
    const matchesNetwork = (str: string) => str.toLowerCase().includes(networkPattern)

    if (matchesNetwork(event.message || '')) return true
    for (const exception of exceptionValues) {
        if (exception.value && matchesNetwork(exception.value)) return true
        if (exception.type && matchesNetwork(exception.type)) return true
    }
    return false
}

export function initSentry() {
    Sentry.init({
        dsn: 'https://0ff87a49e2d2005b7b0eff3b54e42eaa@o4510361420431360.ingest.us.sentry.io/4510361423249408',
        environment: __DEV__ ? 'development' : 'production',
        enabled: !__DEV__,
        sendDefaultPii: true,
        tracesSampleRate: __DEV__ ? 1.0 : 0.2,
        attachStacktrace: true,
        enableNative: true,
        enableAutoSessionTracking: true,
        beforeSend(event) {
            if (__DEV__) {
                log.debug('[Sentry] Captured event', {
                    message: event.message,
                    exceptionType: event.exception?.values?.[0]?.type,
                    exceptionValue: event.exception?.values?.[0]?.value?.substring(0, 100),
                })
            }
            if (isIgnoredError(event)) {
                return null
            }
            if (isNetworkErrorEvent(event)) {
                showNetworkErrorToast(event)
                return null
            }
            return event
        },
    })

    checkAndSetSentryUser()
}

export function setSentryUser(user: UserSession | null) {
    if (user) {
        Sentry.setUser({
            id: user.id,
            email: user.email,
            username: user.name,
        })
    } else {
        Sentry.setUser(null)
    }
}

export function checkAndSetSentryUser() {
    const authRecord = pb.authStore.record as Users | null
    const authToken = pb.authStore.token

    if (authRecord && authToken && pb.authStore.isValid) {
        const user: UserSession = {
            id: authRecord.id,
            name: authRecord.name,
            email: authRecord.email,
            geo_tracking_disabled: authRecord.geo_tracking_disabled ?? false,
        }
        setSentryUser(user)
    }
}

export { Sentry }

export function logAccessDenied(source: string, diagnostics: Record<string, unknown>) {
    log.info(`${source}: Access denied for authenticated user`, diagnostics)
    Sentry.addBreadcrumb({
        category: source,
        level: 'warning',
        message: 'Access denied state reached',
        data: diagnostics,
    })
    Sentry.captureMessage(`${source}: Access denied for authenticated user`, {
        level: 'warning',
        extra: diagnostics,
    })
}

export function captureException(context: string, error: unknown, extra?: Record<string, unknown>) {
    log.errorWithoutSentry(`${context}: ${errorToString(error)}`)
    Sentry.captureException(error, { extra: { context, ...extra } })
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null

function getBaseMessage(error: Record<string, unknown>): string {
    let msg = ''
    if (typeof error.message === 'string') msg = error.message

    const nested = error.originalError ?? error.cause
    if (isRecord(nested) && typeof nested.message === 'string' && nested.message) {
        msg = nested.message
    }
    return msg
}

function formatResponseFieldErrors(data: Record<string, unknown>): string[] {
    const errors: string[] = []
    for (const [field, fieldError] of Object.entries(data)) {
        if (field === 'message' || field === 'status') continue
        if (!isRecord(fieldError)) continue

        if (typeof fieldError.message === 'string') {
            errors.push(`${field}: ${fieldError.message}`)
        } else if (typeof fieldError.code === 'string') {
            errors.push(`${field}: ${fieldError.code.replace(/_/g, ' ')}`)
        }
    }
    return errors
}

// PocketBase's ClientResponseError sets `message = "Something went wrong."`
// whenever it can't pull a useful message from the server response. By itself
// that string is useless to users; pairing it with the HTTP status surfaces
// at least the failure class so we (and they) know whether to retry, check
// connection, or report a bug.
const PB_GENERIC_FALLBACK = 'Something went wrong.'

function withStatus(message: string, error: Record<string, unknown>): string {
    if (typeof error.status === 'number' && error.status > 0) {
        return `${message} (HTTP ${error.status})`
    }
    return message
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: Error parsing probes multiple nested response formats
export function errorToString(error: unknown, fallback = 'An unexpected error occurred'): string {
    if (typeof error === 'string') return error
    if (!isRecord(error)) return fallback

    const msg = getBaseMessage(error)
    const response = error.response

    if (!isRecord(response)) {
        if (msg === PB_GENERIC_FALLBACK) return withStatus(msg, error)
        return msg || fallback
    }

    if (isRecord(response.data)) {
        const fieldErrors = formatResponseFieldErrors(response.data)
        const prefix = msg ? `${msg} ` : ''

        if (fieldErrors.length > 0) return prefix + fieldErrors.join(', ')
        if (typeof response.data.message === 'string') return prefix + response.data.message
        if (typeof response.data.error === 'string') return prefix + response.data.error
    }

    if (typeof error.error === 'string') return error.error
    if (isRecord(error.data) && typeof error.data.message === 'string') return error.data.message

    if (msg === PB_GENERIC_FALLBACK) return withStatus(msg, error)
    return msg || fallback
}

export function reportError(error: unknown, title = 'Error'): void {
    const message = `${title}: ${errorToString(error)}`
    notify.error(message)
    log.errorWithoutSentry(message)
}

function extractFieldErrors(data: Record<string, unknown>): Record<string, string> | null {
    const errors: Record<string, string> = {}

    for (const [field, fieldError] of Object.entries(data)) {
        if (isRecord(fieldError)) {
            if (typeof fieldError.message === 'string') {
                errors[field] = fieldError.message
            } else if (typeof fieldError.code === 'string') {
                errors[field] = `Validation failed: ${fieldError.code}`
            }
        } else if (typeof fieldError === 'string') {
            errors[field] = fieldError
        }
    }

    return Object.keys(errors).length > 0 ? errors : null
}

function getBatchRequestData(data: Record<string, unknown>): Record<string, unknown> | null {
    if (!isRecord(data.requests)) return null
    const firstRequest = data.requests['0']
    if (!isRecord(firstRequest)) return null
    if (!isRecord(firstRequest.response) || !isRecord(firstRequest.response.data)) return null
    return firstRequest.response.data
}

export function extractValidationErrors(error: unknown): Record<string, string> | null {
    if (!isRecord(error)) return null

    if (isRecord(error.response) && isRecord(error.response.data)) {
        const data = error.response.data

        const batchData = getBatchRequestData(data)
        if (batchData) {
            const result = extractFieldErrors(batchData)
            if (result) return result
        }

        if (isRecord(data.data)) {
            const result = extractFieldErrors(data.data)
            if (result) return result
        }
    }

    if (isRecord(error.errors)) {
        return Object.fromEntries(
            Object.entries(error.errors).filter(([, value]) => typeof value === 'string') as [
                string,
                string,
            ][]
        )
    }

    return null
}

export interface FormErrorHandler<T extends FieldValues = FieldValues> {
    setError: UseFormSetError<T>
    getValues: () => T
}

function hasSlugUniqueError(data: Record<string, unknown>): boolean {
    return isRecord(data.slug) && data.slug.code === 'validation_not_unique'
}

function hasDuplicateIdentifierError(error: unknown): boolean {
    if (!isRecord(error) || !isRecord(error.response) || !isRecord(error.response.data)) {
        return false
    }

    const data = error.response.data

    const batchData = getBatchRequestData(data)
    if (batchData && hasSlugUniqueError(batchData)) return true

    if (isRecord(data.data) && hasSlugUniqueError(data.data)) return true

    return false
}

export function handleMutationErrorsWithForm<T extends FieldValues = FieldValues>(
    form: FormErrorHandler<T>
) {
    return (error: unknown) => {
        if (hasDuplicateIdentifierError(error)) {
            notify.error('This identifier is already in use. Please choose a different one.')
            return
        }

        const validationErrors = extractValidationErrors(error)

        if (validationErrors) {
            const formValues = form.getValues()
            const formFields = Object.keys(formValues as Record<string, unknown>)
            const errorFields = Object.keys(validationErrors)

            const unknownFields = errorFields.filter(field => !formFields.includes(field))

            if (unknownFields.length === 0) {
                for (const [field, message] of Object.entries(validationErrors)) {
                    form.setError(field as Path<T>, {
                        type: 'manual',
                        message: message,
                    })
                }
            } else {
                const allErrors = Object.entries(validationErrors)
                    .map(([field, message]) => `${field}: ${message}`)
                    .join('\n')
                reportError(allErrors, 'Validation Error')
            }
        } else {
            reportError(error)
        }
    }
}

export function hasFieldError(error: unknown, field: string): boolean {
    const validationErrors = extractValidationErrors(error)
    return validationErrors ? field in validationErrors : false
}

export function getFieldError(error: unknown, field: string): string | null {
    const validationErrors = extractValidationErrors(error)
    return validationErrors?.[field] || null
}
